<template>
  <CFormGroup
    v-if="label"
    v-bind="formGroupBind"
    :class="computedClasses"
    :style="wrapperAttrs.style"
  >
    <template #label>
      <slot name="label">
        <label v-if="label" :for="safeId" :class="labelClasses">{{ label }}</label>
      </slot>
    </template>
    <template #input>
      <div
        ref="root"
        class="searchable-select"
        :class="rootClasses"
        :style="$attrs.style"
        dir="rtl"
        :id="safeId"
        @keydown="onRootKeydown"
      >
        <slot name="control" :toggle="toggleOpen" :open="open">
          <div
            class="ss-control"
            :class="[inputClass, sizeClass, { 'ss-control--disabled': disabled, 'ss-control--open': open, 'ss-control--multiple': multiple }]"
            tabindex="0"
            role="combobox"
            :aria-expanded="open ? 'true' : 'false'"
            :aria-disabled="disabled ? 'true' : 'false'"
            @click="onControlClick"
            @focus="onControlFocus"
          >
            <div class="ss-value">
              <template v-if="multiple">
                <span v-if="!selectedOptions.length && placeholder" class="ss-placeholder">{{ placeholder }}</span>
                <span v-for="opt in selectedOptions" :key="String(opt.value)" class="ss-tag">
                  {{ opt.label }}
                  <button type="button" class="ss-tag__x" tabindex="-1" aria-label="حذف" @click.stop="deselect(opt.value)">×</button>
                </span>
              </template>
              <template v-else>
                <span v-if="displayLabel" class="ss-single">{{ displayLabel }}</span>
                <span v-else-if="placeholder" class="ss-placeholder">{{ placeholder }}</span>
              </template>
            </div>
            <div class="ss-actions">
              <button
                v-if="clearable && hasValue && !disabled"
                type="button"
                class="ss-clear"
                tabindex="-1"
                aria-label="پاک کردن"
                @click.stop="clear"
              >×</button>
              <span class="ss-chevron" aria-hidden="true"></span>
            </div>
          </div>
        </slot>
        <div v-show="open" class="ss-dropdown" role="listbox" @mousedown.prevent>
          <div v-if="filterable" class="ss-search-wrap">
            <input
              ref="searchInput"
              v-model="query"
              type="search"
              class="ss-search"
              autocomplete="off"
              :placeholder="searchPlaceholder"
              dir="rtl"
              @keydown="onSearchKeydown"
            >
          </div>
          <div v-if="loading" class="ss-status">در حال بارگذاری…</div>
          <template v-else>
            <ul v-if="visibleOptions.length || showCreateOption" class="ss-options">
              <li
                v-for="(opt, idx) in visibleOptions"
                :key="optionKey(opt, idx)"
                class="ss-option"
                :class="{ 'ss-option--active': idx === highlightIndex, 'ss-option--selected': isSelected(opt.value), 'ss-option--disabled': opt.disabled }"
                role="option"
                :aria-selected="isSelected(opt.value) ? 'true' : 'false'"
                @mouseenter="highlightIndex = idx"
                @click="choose(opt)"
              >
                {{ opt.label }}
              </li>
              <li
                v-if="showCreateOption"
                class="ss-option ss-option--create"
                :class="{ 'ss-option--active': highlightIndex === visibleOptions.length }"
                role="option"
                @mouseenter="highlightIndex = visibleOptions.length"
                @click="chooseCustom"
              >
                {{ createOptionLabel }}
              </li>
            </ul>
            <div v-else class="ss-status">{{ emptyText }}</div>
          </template>
        </div>
      </div>
    </template>
    <template v-for="slot in passSlots" #[slot]>
      <slot :name="slot"></slot>
    </template>
  </CFormGroup>
  <div
    v-else
    ref="root"
    class="searchable-select"
    :class="rootClasses"
    :style="$attrs.style"
    dir="rtl"
    @keydown="onRootKeydown"
  >
    <div
      class="ss-control"
      :class="[inputClass, sizeClass, { 'ss-control--disabled': disabled, 'ss-control--open': open, 'ss-control--multiple': multiple }]"
      tabindex="0"
      role="combobox"
      :aria-expanded="open ? 'true' : 'false'"
      :aria-disabled="disabled ? 'true' : 'false'"
      @click="onControlClick"
      @focus="onControlFocus"
    >
      <div class="ss-value">
        <template v-if="multiple">
          <span v-if="!selectedOptions.length && placeholder" class="ss-placeholder">{{ placeholder }}</span>
          <span v-for="opt in selectedOptions" :key="String(opt.value)" class="ss-tag">
            {{ opt.label }}
            <button type="button" class="ss-tag__x" tabindex="-1" aria-label="حذف" @click.stop="deselect(opt.value)">×</button>
          </span>
        </template>
        <template v-else>
          <span v-if="displayLabel" class="ss-single">{{ displayLabel }}</span>
          <span v-else-if="placeholder" class="ss-placeholder">{{ placeholder }}</span>
        </template>
      </div>
      <div class="ss-actions">
        <button
          v-if="clearable && hasValue && !disabled"
          type="button"
          class="ss-clear"
          tabindex="-1"
          aria-label="پاک کردن"
          @click.stop="clear"
        >×</button>
        <span class="ss-chevron" aria-hidden="true"></span>
      </div>
    </div>
    <div v-show="open" class="ss-dropdown" role="listbox" @mousedown.prevent>
      <div v-if="filterable" class="ss-search-wrap">
        <input
          ref="searchInput"
          v-model="query"
          type="search"
          class="ss-search"
          autocomplete="off"
          :placeholder="searchPlaceholder"
          dir="rtl"
          @keydown="onSearchKeydown"
        >
      </div>
      <div v-if="loading" class="ss-status">در حال بارگذاری…</div>
      <template v-else>
        <ul v-if="visibleOptions.length || showCreateOption" class="ss-options">
          <li
            v-for="(opt, idx) in visibleOptions"
            :key="optionKey(opt, idx)"
            class="ss-option"
            :class="{ 'ss-option--active': idx === highlightIndex, 'ss-option--selected': isSelected(opt.value), 'ss-option--disabled': opt.disabled }"
            role="option"
            :aria-selected="isSelected(opt.value) ? 'true' : 'false'"
            @mouseenter="highlightIndex = idx"
            @click="choose(opt)"
          >
            {{ opt.label }}
          </li>
          <li
            v-if="showCreateOption"
            class="ss-option ss-option--create"
            :class="{ 'ss-option--active': highlightIndex === visibleOptions.length }"
            role="option"
            @mouseenter="highlightIndex = visibleOptions.length"
            @click="chooseCustom"
          >
            {{ createOptionLabel }}
          </li>
        </ul>
        <div v-else class="ss-status">{{ emptyText }}</div>
      </template>
    </div>
  </div>
</template>

<script>
import debounce from 'debounce'
import * as allFormMixins from '@/coreui/form/form-mixins'
import { selectProps as baseSelectProps } from '@/coreui/form/form-props'
import CFormGroup from '@/coreui/form/CFormGroup.vue'
import { normalizePersianSearchText, persianSearchIncludes } from '@/utils/persianSearchNormalize'

const { initialValue, ...mixinMap } = allFormMixins
const mixins = Object.values(mixinMap)
const SLOTS = ['prepend', 'prepend-content', 'append-content', 'append', 'label-after-input', 'valid-feedback', 'invalid-feedback', 'description']

let uid = 0

function normalizeOption (raw, index) {
  if (raw == null) return null
  if (typeof raw === 'object' && !Array.isArray(raw)) {
    const label = raw.label != null ? raw.label : raw.value
    return {
      label: label == null ? '' : String(label),
      value: raw.value,
      disabled: !!raw.disabled,
      _idx: index,
    }
  }
  return { label: String(raw), value: raw, disabled: false, _idx: index }
}

export default {
  name: 'SearchableSelect',
  inheritAttrs: false,
  components: { CFormGroup },
  mixins,
  props: {
    ...baseSelectProps,
    multiple: { type: Boolean, default: false },
    clearable: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    filterable: { type: Boolean, default: true },
    remote: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
    allowCustom: { type: Boolean, default: false },
    searchDebounce: { type: Number, default: 300 },
    searchPlaceholder: { type: String, default: 'جستجو…' },
    emptyText: { type: String, default: 'موردی یافت نشد' },
    createLabel: { type: Function, default: null },
    /** Optional client filter override (option, query) => boolean */
    filterMethod: { type: Function, default: null },
    /** Extra class(es) on the control (merged with attrs.class) */
    controlClass: { type: [String, Array, Object], default: null },
    id: String,
  },
  emits: ['update:modelValue', 'update:value', 'input', 'change', 'search'],
  data () {
    return {
      open: false,
      query: '',
      highlightIndex: 0,
      _localId: 'ss-' + (++uid),
      debouncedSearch: null,
    }
  },
  computed: {
    current () {
      if (this.multiple) {
        const v = this.modelValue !== undefined ? this.modelValue : this.value
        return Array.isArray(v) ? v : (v == null || v === '' ? [] : [v])
      }
      return this.modelValue !== undefined ? this.modelValue : this.value
    },
    normalizedOptions () {
      const list = Array.isArray(this.options) ? this.options : []
      return list.map(normalizeOption).filter(Boolean)
    },
    selectedOptions () {
      if (!this.multiple) return []
      const vals = this.current
      return this.normalizedOptions.filter((o) => vals.some((v) => this.valuesEqual(v, o.value)))
    },
    displayLabel () {
      if (this.multiple) return ''
      const opt = this.normalizedOptions.find((o) => this.valuesEqual(o.value, this.current))
      return opt ? opt.label : (this.current != null && this.current !== '' ? String(this.current) : '')
    },
    hasValue () {
      if (this.multiple) return this.current.length > 0
      return this.current !== undefined && this.current !== null && this.current !== ''
    },
    visibleOptions () {
      if (this.remote) return this.normalizedOptions
      const q = this.query.trim()
      if (!this.filterable || !q) return this.normalizedOptions
      if (this.filterMethod) {
        return this.normalizedOptions.filter((o) => this.filterMethod(o, q))
      }
      return this.normalizedOptions.filter((o) => persianSearchIncludes(o.label, q) || persianSearchIncludes(String(o.value), q))
    },
    showCreateOption () {
      if (!this.allowCustom || !this.query.trim()) return false
      const q = this.query.trim()
      return !this.normalizedOptions.some((o) => persianSearchIncludes(o.label, q) && normalizePersianSearchText(o.label) === normalizePersianSearchText(q))
    },
    createOptionLabel () {
      const q = this.query.trim()
      if (this.createLabel) return this.createLabel(q)
      return 'افزودن «' + q + '»'
    },
    passSlots () {
      return SLOTS.filter((s) => this.$slots[s])
    },
    formGroupBind () {
      return {
        append: this.append,
        prepend: this.prepend,
        validFeedback: this.validFeedback,
        invalidFeedback: this.invalidFeedback,
        tooltipFeedback: this.tooltipFeedback,
        description: this.description,
        wrapperClasses: this.wrapperClasses,
      }
    },
    sizeClass () {
      if (this.size) return 'ss-control--' + this.size
      return null
    },
    inputClass () {
      const fromAttrs = this.$attrs.class
      const base = this.controlClass || fromAttrs || (this.custom ? 'custom-select' : 'form-control')
      return base
    },
    rootClasses () {
      return {
        'searchable-select--multiple': this.multiple,
        'searchable-select--disabled': this.disabled,
      }
    },
    safeId () {
      return this.id || this._localId
    },
  },
  watch: {
    query (q) {
      if (this.debouncedSearch) this.debouncedSearch(q)
      this.highlightIndex = 0
    },
    open (v) {
      if (v) {
        this.highlightIndex = 0
        this.$nextTick(() => {
          const el = this.$refs.searchInput
          if (el) el.focus()
        })
      } else {
        this.query = ''
      }
    },
  },
  created () {
    this.debouncedSearch = debounce((q) => {
      this.$emit('search', q)
    }, this.searchDebounce)
  },
  mounted () {
    document.addEventListener('mousedown', this.onDocMouseDown)
  },
  beforeUnmount () {
    document.removeEventListener('mousedown', this.onDocMouseDown)
  },
  methods: {
    valuesEqual (a, b) {
      if (a === b) return true
      if (a == null && b == null) return true
      return String(a) === String(b)
    },
    isSelected (value) {
      if (this.multiple) return this.current.some((v) => this.valuesEqual(v, value))
      return this.valuesEqual(this.current, value)
    },
    optionKey (opt, idx) {
      return String(opt.value) + '-' + idx
    },
    onDocMouseDown (e) {
      if (!this.open) return
      const root = this.$refs.root
      if (root && !root.contains(e.target)) this.close()
    },
    toggleOpen () {
      if (this.disabled) return
      this.open = !this.open
    },
    openDropdown () {
      if (this.disabled) return
      this.open = true
    },
    close () {
      this.open = false
    },
    onControlClick () {
      this.toggleOpen()
    },
    onControlFocus () {
      // single click opens; keyboard users can type when filterable
    },
    emitValue (value, nativeEvent) {
      this.$emit('update:modelValue', value)
      this.$emit('update:value', value, nativeEvent)
      this.$emit('input', value, nativeEvent)
      this.$emit('change', value, nativeEvent)
    },
    choose (opt) {
      if (opt.disabled) return
      if (this.multiple) {
        const next = [...this.current]
        const i = next.findIndex((v) => this.valuesEqual(v, opt.value))
        if (i >= 0) next.splice(i, 1)
        else next.push(opt.value)
        this.emitValue(next)
      } else {
        this.emitValue(opt.value)
        this.close()
      }
    },
    chooseCustom () {
      const raw = this.query.trim()
      if (!raw) return
      let val = raw
      const sample = this.normalizedOptions.find((o) => typeof o.value === 'number')
      if (sample && /^-?\d+(\.\d+)?$/.test(raw)) val = Number(raw)
      if (this.multiple) {
        const next = [...this.current]
        if (!next.some((v) => this.valuesEqual(v, val))) next.push(val)
        this.emitValue(next)
      } else {
        this.emitValue(val)
        this.close()
      }
    },
    deselect (value) {
      if (!this.multiple) return
      const next = this.current.filter((v) => !this.valuesEqual(v, value))
      this.emitValue(next)
    },
    clear () {
      if (this.multiple) this.emitValue([])
      else this.emitValue('')
    },
    onSearchKeydown (e) {
      const total = this.visibleOptions.length + (this.showCreateOption ? 1 : 0)
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        if (total) this.highlightIndex = (this.highlightIndex + 1) % total
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        if (total) this.highlightIndex = (this.highlightIndex - 1 + total) % total
      } else if (e.key === 'Enter') {
        e.preventDefault()
        if (this.highlightIndex < this.visibleOptions.length) {
          this.choose(this.visibleOptions[this.highlightIndex])
        } else if (this.showCreateOption) {
          this.chooseCustom()
        }
      } else if (e.key === 'Escape') {
        e.preventDefault()
        this.close()
      }
    },
    onRootKeydown (e) {
      if (e.target.classList && e.target.classList.contains('ss-search')) return
      if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        this.openDropdown()
      } else if (e.key === 'Escape') {
        this.close()
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.searchable-select {
  position: relative;
  width: 100%;
  font-family: inherit;
}

.ss-control {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  min-height: 38px;
  padding: 6px 10px;
  border: 1px solid var(--panel-border, #e5e7eb);
  border-radius: 10px;
  background: var(--panel-surface, #fff);
  color: var(--panel-text, #111827);
  font-size: 13px;
  cursor: pointer;
  outline: none;
  box-sizing: border-box;
}

.ss-control:focus,
.ss-control--open {
  border-color: #111111;
}

.ss-control--disabled {
  opacity: 0.6;
  cursor: not-allowed;
  background: #f9fafb;
}

.ss-control--sm,
.ss-control--small {
  min-height: 32px;
  padding: 4px 8px;
  font-size: 12px;
}

.ss-value {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  align-items: center;
  text-align: right;
}

.ss-placeholder {
  color: var(--panel-muted, #9ca3af);
}

.ss-single {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ss-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 6px;
  border-radius: 6px;
  background: #f3f4f6;
  font-size: 12px;
}

.ss-tag__x {
  border: 0;
  background: transparent;
  cursor: pointer;
  padding: 0 2px;
  line-height: 1;
  color: #6b7280;
}

.ss-actions {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}

.ss-clear {
  border: 0;
  background: transparent;
  color: #6b7280;
  cursor: pointer;
  font-size: 16px;
  line-height: 1;
  padding: 0 2px;
}

.ss-chevron {
  width: 0;
  height: 0;
  border-inline-start: 5px solid transparent;
  border-inline-end: 5px solid transparent;
  border-top: 5px solid #6b7280;
}

.ss-dropdown {
  position: absolute;
  z-index: 1050;
  inset-inline: 0;
  top: calc(100% + 4px);
  max-height: 280px;
  display: flex;
  flex-direction: column;
  border: 1px solid var(--panel-border, #e5e7eb);
  border-radius: 10px;
  background: var(--panel-surface, #fff);
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
  overflow: hidden;
}

.ss-search-wrap {
  padding: 8px;
  border-bottom: 1px solid var(--panel-border, #f3f4f6);
}

.ss-search {
  width: 100%;
  min-height: 34px;
  padding: 6px 10px;
  border: 1px solid var(--panel-border, #e5e7eb);
  border-radius: 8px;
  font: inherit;
  font-size: 13px;
  outline: none;
}

.ss-search:focus {
  border-color: #111111;
}

.ss-options {
  list-style: none;
  margin: 0;
  padding: 4px 0;
  overflow-y: auto;
  max-height: 220px;
}

.ss-option {
  padding: 8px 12px;
  cursor: pointer;
  font-size: 13px;
  text-align: right;
}

.ss-option--active {
  background: #f3f4f6;
}

.ss-option--selected {
  font-weight: 700;
}

.ss-option--disabled {
  opacity: 0.45;
  pointer-events: none;
}

.ss-option--create {
  color: #2563eb;
  border-top: 1px dashed var(--panel-border, #e5e7eb);
}

.ss-status {
  padding: 12px;
  text-align: center;
  color: var(--panel-muted, #6b7280);
  font-size: 13px;
}
</style>
